<template>
  <div
    class="d-flex justify-content-end mb-2"
    v-if="!loading"
  >
    <template v-if="unreadCount > 0">
      {{ t('unreadCount', { range: t(showAll ? 'upTo1000' : 'last200'), count: unreadCount }) }}
    </template>
    <template v-else>
      {{ t('noUnread', { range: t(showAll ? 'upTo1000' : 'last200') }) }}
    </template>
  </div>
  <div
    v-if="!showAll && hasMore"
    class="d-flex justify-content-end mb-2"
  >
    <button
      class="btn btn-sm btn-outline-warning"
      @click="loadAll"
    >
      {{ t('allMessages') }}
    </button>
  </div>
  <div
    class="messages-block"
    v-if="model.data?.length"
  >
    <div
      class="one-mess"
      v-for="item in model.data"
      :key="item.id"
      :ref="el => setRowRef(el, item)"
    >
      <div
        :class="item.cl"
        class="card mb-2"
      >
        <div class="text-mess">
          <span
            v-if="!item.was_read"
            class="new-mess"
            >{{ t('new') }}</span
          >
          <div class="only-text-mess p-2">
            <div v-html="item.message" />
            <div class="footer-mess d-flex justify-content-between align-items-center">
              <span
                v-if="item.created_at"
                class="created-mess fs-12"
                >{{ item.created_at }}</span
              >
              <RouterLink
                v-if="item.ref_link"
                :to="item.ref_link"
                class="link-mess"
                active-class=""
                @click="emit('modalstack:close')"
              >
                {{ t('goTo') }}
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!showAll && hasMore"
      class="text-center mt-3"
    >
      <button
        class="btn btn-sm btn-outline-secondary"
        @click="loadAll"
      >
        {{ t('allMessages') }}
      </button>
    </div>
  </div>
  <div v-else-if="loading">
    <div class="text-center py-5">
      <i class="bi bi-arrow-clockwise spinning"></i>
      <div class="mt-2">{{ t('loadingMessages') }}</div>
    </div>
  </div>
  <div v-else>{{ t('noMessages') }}</div>
</template>

<script setup>
import { onMounted, ref, computed, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { commons } from '@/lib/transformer'
import { useApi } from '@/composables/api'
import dompurify from 'dompurify'

const ids = new Set()
const { t } = useI18n()
const observer = ref(null)
const clLevels = { 1: 'danger', 2: 'warning', 3: 'primary' }
const showAll = ref(false)
const loading = ref(true)

const { model, refresh } = useApi({
  url: 'notifications',
  data: {},
  pick: d => {
    d.data = d.data.map(d => {
      if (d.created_at) {
        d.created_at = commons.dateLocal(d.created_at, 'Pp')
      }
      d.cl = clLevels[d.level]
      d.message = dompurify.sanitize(d.message)
      return d
    })
    loading.value = false
    return d
  },
})

const { refresh: doRead } = useApi({
  url: 'notifications/read',
  method: 'put',
  data: {},
  transform: () => ({ notifications: Array.from(ids.values()).map(id => ({ id })) }),
})

const hasMore = computed(() => {
  const total = model.value?.total_count || 0
  return total > 200
})

const unreadCount = computed(() => {
  return model.value?.data?.filter(message => !message.was_read).length || 0
})

const emit = defineEmits(['modalstack:close'])
const props = defineProps({
  modalstack: {
    type: Boolean,
    default: false,
  },
  class: {
    type: String,
    default: '',
  },
  headerText: {
    type: String,
    default: '',
  },
  localHasUnread: {
    type: [Boolean, Object],
    default: false,
  },
})

const setRowRef = (el, row) => {
  if (el && !row.was_read) {
    el.__dataRow = row.id
    observer.value.observe(el)
  }
}

const onIntersect = entries => {
  for (const entry of entries) {
    if (!entry.isIntersecting) {
      continue
    }
    ids.add(entry.target.__dataRow)
  }
}

function markModelRead() {
  if (!model.value?.data || ids.size === 0) return
  model.value.data = model.value.data.map(m => (ids.has(m.id) ? { ...m, was_read: 1 } : m))
  ids.clear()
  if (props.localHasUnread && typeof props.localHasUnread === 'object') {
    props.localHasUnread.value = !!model.value.data.some(m => !m.was_read)
  }
}

async function loadAll() {
  try {
    await refresh({ query: { mode: 'all' } })
    showAll.value = true
  } catch (e) {
    console.error('loadAll error', e)
  }
}

onMounted(() => {
  observer.value = new IntersectionObserver(onIntersect, {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  })

  refresh()
})
onUnmounted(() => {
  observer.value.disconnect()
  if (ids.size > 0) {
    doRead()
    markModelRead()
    const currentUnread = unreadCount.value - ids.size
    props.localHasUnread.value = Math.max(currentUnread, 0) > 0
  }
})
</script>

<i18n>
{
  "ru": {
    "unreadCount": "Непрочитанных сообщений{range}: {count}",
    "noUnread": "Нет непрочитанных сообщений{range}",
    "upTo1000": " (до 1000)",
    "last200": " из последних 200",
    "allMessages": "Все сообщения",
    "new": "новое",
    "goTo": "перейти",
    "loadingMessages": "Загрузка сообщений...",
    "noMessages": "Нет сообщений"
  },
  "en": {
    "unreadCount": "Unread messages{range}: {count}",
    "noUnread": "No unread messages{range}",
    "upTo1000": " (up to 1000)",
    "last200": " from the latest 200",
    "allMessages": "All messages",
    "new": "new",
    "goTo": "go to",
    "loadingMessages": "Loading messages...",
    "noMessages": "No messages"
  }
}
</i18n>
